import classNames from 'classnames';
import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { BotIcon } from '@/components/ui/bot-icon';
import { DemoPill, StateMessage } from '@/components/ui/controls';
import { Badge, Card, PageHeader, SubNav } from '@/components/ui/page';
import { StageTag } from '@/components/ui/stage';
import { btn } from '@/components/ui/styles';
import { mockCalls } from '@/mocks/lead-extras';
import type { LeadDelivery } from '@/pages/messaging/api-types';
import { channelName } from '@/pages/messaging/channels';
import { ResultBadge } from '@/pages/messaging/parts';
import type { LeadDetail, TelegramChatDetail } from '@/types/api';
import { formatDate, formatDateTime, leadTitle, shownEmail } from '@/utils/format';
import { showV2 } from '@/constants/routes';
import { channelLabel, chatKindLabel, sourceLabel, stageLabel } from '@/utils/labels';
import { useApi } from '@/utils/use-api';

import { FollowUps, FollowUpsDemo } from './follow-ups';
import { CasinoList, DetailsList, Empty, FormAnswers, FormDemo, SourceBody, SummaryBody, SummaryDemo, TicketList } from './lead-parts';
import { EditLeadPanel } from './new-lead';
import { EmailUpdatesControl } from './email-updates';
import { StageControl } from './stage-control';
import AssistantGroupButton from '@/features/assistant/assistant-group-button';

import { TelegramLinkButton } from './telegram-link';

// Calls (CALL-1…4) is a V2 tab: hidden unless V2 is switched on.
const TABS = ['', 'conversation', 'follow-ups', ...(showV2 ? ['calls'] : []), 'messages', 'activity'];

// A text link in a card header: 44px tall on a phone (UI-1).
const headerLink = classNames(btn.link, 'inline-flex min-h-11 items-center whitespace-nowrap md:min-h-0');

/** One linked chat's whole conversation (TG-3), styled as on the Conversations page. */
const ChatHistory = ({ chatId }: { chatId: number }) => {
  const { data: chat, error } = useApi<TelegramChatDetail>(`/telegram/chats/${chatId}`);
  return (
    <Card
      title={chat?.title ?? 'Telegram chat'}
      aside={
        <Link to={`/conversations?status=linked&chat=${chatId}`} className={headerLink}>
          Open in Conversations
        </Link>
      }
    >
      {chat ? (
        <>
          <p className="mb-4 break-words text-xs text-fg-faint">
            {chatKindLabel(chat.kind)} · {chat.messages} messages
            {chat.confirmed_at ? ` · linked ${formatDate(chat.confirmed_at)}${chat.confirmed_by ? ` by ${chat.confirmed_by}` : ''}` : ''}
          </p>
          <ol className="space-y-3">
            {chat.message_history.map((m) => (
              <li key={m.message_id} className={classNames('flex flex-col', m.from_team ? 'items-end' : 'items-start')}>
                <span className="mb-0.5 inline-flex items-center gap-1 text-xs text-fg-faint">
                  {m.from_bot && <BotIcon />}
                  {m.from_bot ? 'Prisma bot' : m.from_username || 'unknown'}
                  {m.from_team && !m.from_bot ? ' (team)' : ''} · {formatDateTime(m.sent_at)}
                  {m.edited_at ? ' · edited' : ''}
                </span>
                <p
                  className={classNames(
                    'max-w-[75%] whitespace-pre-wrap break-words rounded-xl px-3 py-2 text-sm text-fg',
                    m.from_bot ? 'bg-info/15' : m.from_team ? 'bg-brand/10' : 'bg-surface-inset',
                  )}
                >
                  {m.text}
                </p>
              </li>
            ))}
          </ol>
        </>
      ) : (
        <Empty>{error ?? 'Loading…'}</Empty>
      )}
    </Card>
  );
};

/** The record, side by side (LEAD-*, ATT-*, TG-4, CAS-3, REM-1). */
const OverviewTab = ({ lead, base, onChange }: { lead: LeadDetail; base: string; onChange: () => void }) => {
  const next = lead.follow_ups.find((f) => !f.done_at);
  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
      <div className="space-y-6">
        <Card title="Stage">
          <StageControl lead={lead} onChange={onChange} />
        </Card>
        <Card title="Email updates" notYet="email">
          <EmailUpdatesControl lead={lead} onChange={onChange} />
        </Card>
        <Card title="Latest AI conversation summary" aside={<SummaryDemo lead={lead} />} notYet="ai">
          <SummaryBody lead={lead} />
        </Card>
        <Card title="Details">
          <DetailsList lead={lead} />
        </Card>
        <Card title="Source">
          <SourceBody lead={lead} />
        </Card>
      </div>
      <div className="space-y-6">
        <Card
          title="Next follow-up"
          aside={
            <Link to={`${base}/follow-ups`} className={headerLink}>
              All follow-ups
            </Link>
          }
        >
          {next ? (
            <p className="text-sm text-fg">
              <span className="text-fg-strong">{formatDateTime(next.due_at)}</span>
              {next.note && <span className="text-fg-muted"> · {next.note}</span>}
            </p>
          ) : (
            <Empty>No open follow-ups.</Empty>
          )}
        </Card>
        <Card title="Contact form answers" aside={<FormDemo lead={lead} />}>
          <FormAnswers lead={lead} />
        </Card>
        <Card title={`Casinos (${lead.casinos.length})`}>
          <CasinoList lead={lead} linked />
        </Card>
        {lead.tickets?.length > 0 && (
          <Card title={`Tickets (${lead.tickets.length})`}>
            <TicketList lead={lead} />
          </Card>
        )}
      </div>
    </div>
  );
};

/** Every linked chat in full (TG-3): the mockup's "link to the full chat". */
const ConversationTab = ({ lead }: { lead: LeadDetail }) =>
  lead.chats.length ? (
    <div className="max-w-3xl space-y-6">
      {lead.chats.map((c) => (
        <ChatHistory key={c.chat_id} chatId={c.chat_id} />
      ))}
      <TelegramLinkButton leadId={lead.id} linked={lead.telegram_linked} />
    </div>
  ) : (
    <StateMessage title="No conversation yet">
      No Telegram group is linked to this lead (TG-1, TG-2).{' '}
      <Link to="/conversations" className="text-brand hover:underline">
        Confirm a chat in Conversations
      </Link>
      , or send the owner their bot link:
      <div className="mt-3 flex justify-center">
        <TelegramLinkButton leadId={lead.id} linked={lead.telegram_linked} />
      </div>
    </StateMessage>
  );

/** Calls with the lead (CALL-1…4): made up until calls are captured in V2. */
const CallsTab = ({ lead }: { lead: LeadDetail }) => {
  const calls = mockCalls(lead);
  return (
    <Card
      className="max-w-3xl"
      title="Calls"
      aside={
        <span className="flex items-center gap-2">
          <Badge>V2</Badge>
          <DemoPill title="Made up: calls are not captured yet (CALL-1…4)">Demo</DemoPill>
        </span>
      }
    >
      {calls.length ? (
        <ul className="divide-y divide-line">
          {calls.map((c) => (
            <li key={c.id} className="py-3 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm text-fg-strong">{c.title}</p>
                <p className="text-xs text-fg-faint">
                  {formatDateTime(c.at)} · {c.minutes} min
                </p>
              </div>
              <p className="text-xs text-fg-muted">With {c.people.join(' and ')}</p>
              <p className="mt-2 text-sm text-fg">{c.notes}</p>
              <p className="mt-1 text-xs text-fg-faint">Linked: {c.linkedBy}</p>
            </li>
          ))}
        </ul>
      ) : (
        <Empty>No calls yet.</Empty>
      )}
    </Card>
  );
};

/** The messages the lead got by email, Telegram or Owner Studio notification, one row per channel (OUT-1, OUT-5, OUT-6). */
const MessagesTab = ({ lead }: { lead: LeadDetail }) => {
  const { data, error } = useApi<LeadDelivery[]>('/messages/deliveries', { lead_id: lead.id });
  const rows = data ?? [];
  return (
    <Card
      className="max-w-3xl"
      title="Messages sent to this lead"
      aside={rows.some((d) => d.demo) && <DemoPill title="Some of these come from the local demo seed">Demo</DemoPill>}
    >
      {error && !data && <p className="text-sm text-danger">Messages could not be loaded.</p>}
      {rows.length ? (
        <ul className="divide-y divide-line">
          {rows.map((d) => (
            <li key={`${d.message_id}-${d.channel}-${d.created_at}`} className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
              <div className="min-w-0">
                <Link to={`/messaging/messages/${d.message_id}`} className="block break-words py-1 text-sm text-fg-strong hover:text-brand md:inline md:py-0">
                  {d.title}
                </Link>
                <p className="text-xs text-fg-muted">
                  {channelName(d.channel)} · {formatDateTime(d.sent_at ?? d.created_at)}
                  {d.trigger_name && ` · automated: ${d.trigger_name}`}
                </p>
              </div>
              <ResultBadge result={d} />
            </li>
          ))}
        </ul>
      ) : (
        data && <Empty>Nothing sent yet.</Empty>
      )}
      <Link to="/messaging" className={classNames(btn.link, 'mt-4 inline-flex min-h-11 items-center md:min-h-0')}>
        Go to Messaging
      </Link>
    </Card>
  );
};

type Event = { at: string; title: string; detail?: string };

/** Everything that happened to the lead, from the real record, newest first. */
const activity = (lead: LeadDetail): Event[] => {
  const ev: Event[] = [{ at: lead.created_at, title: 'Added to Prisma' }];
  for (const t of lead.touches) {
    ev.push({
      at: t.occurred_at,
      title: t.channel === 'manual' ? `Added by ${t.added_by || 'a team member'}` : `Arrived via ${channelLabel(t.channel).toLowerCase()}`,
      detail: [sourceLabel(t.source), t.event, t.utm_campaign && `campaign ${t.utm_campaign}`].filter(Boolean).join(' · '),
    });
  }
  for (const f of lead.contact_submissions) ev.push({ at: f.submitted_at, title: 'Sent the contact form', detail: `${f.answers.length} answers` });
  if (lead.stage !== 'new') {
    // Clarifications is set by a person (Q4); the other stages come from Owner Hub.
    const by = lead.stage_override ? `Set by ${lead.stage_override_by || 'a team member'}` : 'From Owner Hub (CAS-4)';
    ev.push({ at: lead.stage_changed_at, title: `Stage became ${stageLabel(lead.stage)}`, detail: by });
  }
  for (const c of lead.casinos) {
    if (c.launched_at) ev.push({ at: c.launched_at, title: `${c.brand_name || 'A casino'} went live` });
  }
  for (const c of lead.chats) {
    if (c.confirmed_at) ev.push({ at: c.confirmed_at, title: `Telegram chat “${c.title}” linked`, detail: c.confirmed_by && `by ${c.confirmed_by}` });
  }
  if (lead.latest_summary) ev.push({ at: lead.latest_summary.created_at, title: 'AI summary written', detail: lead.latest_summary.summary });
  for (const f of lead.follow_ups) {
    ev.push({
      at: f.created_at,
      title: 'Follow-up set',
      detail: [`for ${formatDateTime(f.due_at)}`, f.note, f.created_by && `by ${f.created_by}`].filter(Boolean).join(' · '),
    });
    if (f.reminded_at) ev.push({ at: f.reminded_at, title: 'Reminder sent to the team chat', detail: f.note });
    if (f.done_at) ev.push({ at: f.done_at, title: 'Follow-up done', detail: f.note });
  }
  return ev.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
};

const ActivityTab = ({ lead }: { lead: LeadDetail }) => (
  <Card className="max-w-3xl" title="Activity">
    <ol className="space-y-4 border-l border-line pl-5">
      {activity(lead).map((e, i) => (
        <li key={i} className="relative">
          <span className="absolute -left-[25px] top-1.5 h-2 w-2 rounded-full bg-viz-muted" aria-hidden />
          <p className="text-xs text-fg-faint">{formatDateTime(e.at)}</p>
          <p className="text-sm text-fg-strong">{e.title}</p>
          {e.detail && <p className="line-clamp-2 text-sm text-fg-muted">{e.detail}</p>}
        </li>
      ))}
    </ol>
  </Card>
);

/**
 * One lead on a full page (LEAD-*, TG-3…5, REM-1, CALL-1, OUT-5): the record, the
 * whole conversation, follow-ups, calls, the updates it received and its history, as
 * tabs under /leads/:id. Calls and updates are made up until those parts exist.
 */
const LeadPage = () => {
  const { id = '', tab = '' } = useParams();
  const { data: lead, error, loading, reload } = useApi<LeadDetail>(`/leads/${encodeURIComponent(id)}`);
  const base = `/leads/${encodeURIComponent(id)}`;
  const [editing, setEditing] = useState(false);

  if (!TABS.includes(tab)) return <Navigate to={base} replace />;
  if (!lead || lead.id !== id) {
    const missing = error === 'Not found.';
    return (
      <div>
        <PageHeader title={missing ? 'Lead not found' : 'Lead'} back={{ to: '/leads', label: 'Leads' }} />
        {error ? (
          <StateMessage title={missing ? 'Lead not found' : 'The lead could not be loaded'} tone={missing ? 'neutral' : 'error'}>
            {missing ? 'The link may be old, or the lead was merged.' : error}{' '}
            <Link to="/leads" className="text-brand hover:underline">
              Back to Leads
            </Link>
          </StateMessage>
        ) : (
          loading && <div className="h-40 animate-pulse rounded-card bg-surface shadow-e1" />
        )}
      </div>
    );
  }

  const openFollowUps = lead.follow_ups.filter((f) => !f.done_at).length;
  return (
    <div>
      <PageHeader
        back={{ to: '/leads', label: 'Leads' }}
        title={<span className="break-words">{leadTitle(lead.full_name, lead.email)}</span>}
        description={
          <span className="break-words">
            {lead.full_name ? shownEmail(lead.email) || 'No email' : ''}
            {lead.telegram_handle && `${lead.full_name ? ' · ' : ''}@${lead.telegram_handle}`}
            {lead.company && ` · ${lead.company}`}
          </span>
        }
        actions={
          <span className="flex flex-wrap items-center gap-3">
            <span className="flex flex-wrap items-center gap-2">
              <StageTag stage={lead.stage} platform={lead.platform_stage} blocked={Boolean(lead.blocked_at)} />
              <span className="text-xs text-fg-faint">since {formatDate(lead.stage_changed_at)}</span>
            </span>
            <AssistantGroupButton leadId={lead.id} onDone={reload} />
            <button type="button" className={btn.secondary} onClick={() => setEditing(true)}>
              Edit
            </button>
          </span>
        }
      >
        <SubNav
          label="Lead"
          items={[
            { to: base, label: 'Overview', end: true },
            { to: `${base}/conversation`, label: 'Conversation', count: lead.chats.reduce((n, c) => n + c.messages, 0) },
            { to: `${base}/follow-ups`, label: 'Follow-ups', count: openFollowUps },
            ...(showV2 ? [{ to: `${base}/calls`, label: 'Calls' }] : []),
            { to: `${base}/messages`, label: 'Messages' },
            { to: `${base}/activity`, label: 'Activity' },
          ]}
        />
      </PageHeader>
      {editing && (
        <EditLeadPanel
          lead={lead}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            reload();
          }}
        />
      )}
      <div className={classNames('transition-opacity', loading && 'opacity-60')}>
        {tab === '' && <OverviewTab lead={lead} base={base} onChange={reload} />}
        {tab === 'conversation' && <ConversationTab lead={lead} />}
        {tab === 'follow-ups' && (
          <Card className="max-w-2xl" title="Follow-ups" aside={<FollowUpsDemo lead={lead} />}>
            <FollowUps lead={lead} onChange={reload} showDone />
          </Card>
        )}
        {tab === 'calls' && <CallsTab lead={lead} />}
        {tab === 'messages' && <MessagesTab lead={lead} />}
        {tab === 'activity' && <ActivityTab lead={lead} />}
      </div>
    </div>
  );
};

export default LeadPage;
